import React from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  FaRocket,
  FaUsers,
  FaAward,
  FaLightbulb,
  FaChartLine,
  FaHandshake,
  FaStar,
  FaGlobe,
  FaShieldAlt,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const AboutUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, duration: 0.8 },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  const fallingVariants = {
    fall: {
      y: [-100, 1000],
      rotate: [0, 180],
      opacity: [0, 0.3, 0],
      transition: {
        duration: 15 + Math.random() * 10,
        repeat: Infinity,
        delay: Math.random() * 5,
        ease: "linear",
      },
    },
  };


  const stats = [
    { number: "8+", label: "Years Experience", icon: FaAward },
    { number: "500+", label: "Projects Completed", icon: FaCheckCircle },
    { number: "50+", label: "Team Experts", icon: FaUsers },
    { number: "99%", label: "Client Retention", icon: FaShieldAlt },
  ];

  const values = [
    {
      icon: FaRocket,
      title: "Innovation Driven",
      description:
        "Pioneering cutting-edge digital strategies that keep you ahead of the competition.",
      color: "from-amber-500 to-orange-500",
    },
    {
      icon: FaChartLine,
      title: "Results Focused",
      description:
        "Data-driven approaches that deliver measurable ROI and business growth.",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: FaHandshake,
      title: "Partnership Approach",
      description:
        "Building long-term relationships based on trust and mutual success.",
      color: "from-emerald-500 to-green-500",
    },
    {
      icon: FaUsers,
      title: "Expert Team",
      description:
        "Seasoned professionals with deep industry expertise and proven track records.",
      color: "from-purple-500 to-pink-500",
    },
  ];

  const services = [
    "Search Engine Optimization (SEO)",
    "Pay-Per-Click Advertising",
    "Social Media Marketing",
    "Content Marketing",
    "Email Marketing",
    "Conversion Rate Optimization",
    "Marketing Analytics",
    "Brand Strategy",
  ];

  return (
    <>
      <Navbar />

      <section
        id="about"
        className="pt-10 pb-20 bg-gradient-to-br from-amber-50 via-white to-orange-50
        text-gray-800 overflow-hidden relative rounded-3xl mx-4 my-8"
      >
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden rounded-3xl">
          {[...Array(25)].map((_, i) => (
            <motion.div
              key={i}
              variants={fallingVariants}
              animate="fall"
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                fontSize: `${8 + Math.random() * 12}px`,
              }}
            >
              {[FaStar, FaRocket, FaChartLine, FaUsers, FaAward][i % 5]({
                className: `text-amber-${200 +
                  Math.floor(Math.random() * 3) * 100} opacity-40`,
              })}
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="max-w-7xl mx-auto"
          >
            {/* HEADER */}
            <div className="text-center mb-16">
              <motion.h1
                variants={itemVariants}
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              >
                About{" "}
                <span className="bg-gradient-to-r from-amber-600 to-orange-600
                bg-clip-text text-transparent">
                  Us
                </span>
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
              >
                We are a premier digital marketing agency dedicated to driving
                exceptional results through innovative strategies and data-driven insights.
              </motion.p>
            </div>

            {/* ==============================
                MAIN 2-COLUMN LAYOUT FIXED
            =============================== */}
            <div className="grid lg:grid-cols-2 gap-12 items-start mb-20 w-full">

  {/* LEFT = Our Story + Our Expertise */}
  <motion.div variants={itemVariants} className="space-y-12 w-full">

    {/* OUR STORY */}
    <div>
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-amber-50 shadow-sm space-y-6">

        <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>

        <p className="text-lg text-gray-700 leading-relaxed">
          Welcome to <strong className="text-amber-600">AmpliNova</strong> -
          Where Ideas Transform Into Intelligent Realities. At AmpliNova, we combine human
          creativity with AI-driven innovation to help businesses grow, scale, and stand
          out in a digital-first world. We're not just another IT & digital marketing
          company — we are your technology partner, building smart solutions that adapt,
          evolve, and accelerate your success.
        </p>

        <p className="text-lg text-gray-700 leading-relaxed">
          🔶 <strong className="text-amber-600">What We Do</strong><br />
          • AI-powered IT solutions<br />
          • Smart digital marketing<br />
          • Automation systems<br />
          • Branding strategies
        </p>

        <p className="text-lg text-gray-700 leading-relaxed">
          ✨ Explore the future with AmpliNova.<br />
          <strong className="text-amber-600">
            Your vision. Our innovation. Powered by AI.
          </strong>
        </p>

        <p className="text-lg text-gray-700 leading-relaxed">
          🔶 <strong className="text-amber-600">What Makes Us Different</strong><br />
          AmpliNova delivers innovative, AI-powered solutions tailored to each business,
          focused on results and ROI. We guide you end-to-end from strategy to execution
          with clarity and confidence, accelerating your growth with intelligent workflows
          and automation.
        </p>

      </div>
    </div>

    {/* OUR EXPERTISE */}
    <motion.div
      variants={itemVariants}
      className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-100 shadow-sm w-full"
    >
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Expertise</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service, index) => (
          <motion.div
            key={index}
            className="flex items-center gap-3 group"
            whileHover={{ x: 5 }}
          >
            <div className="w-2 h-2 bg-amber-500 rounded-full" />
            <span className="text-gray-700 group-hover:text-gray-900">
              {service}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>

  </motion.div>

  {/* RIGHT = Stats + Values */}
  <div className="space-y-12 w-full">

    {/* STATS */}
    <motion.div
      variants={containerVariants}
      className="grid grid-cols-2 gap-6 w-full"
    >
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          variants={itemVariants}
          className="bg-white/80 rounded-2xl p-6 border border-amber-100 shadow text-center"
        >
          <stat.icon className="w-8 h-8 text-amber-600 mx-auto mb-3" />
          <div className="text-3xl font-bold">{stat.number}</div>
          <div className="text-gray-600 text-sm">{stat.label}</div>
        </motion.div>
      ))}
    </motion.div>

    {/* VALUES */}
    <motion.div variants={itemVariants} className="w-full">
      <h3 className="text-2xl font-bold text-gray-900 mb-8">Our Values</h3>

      <div className="space-y-6">
        {values.map((value, index) => (
          <motion.div
            key={index}
            className="flex items-start gap-4 p-6 rounded-2xl border border-amber-100 bg-white/80 shadow-sm"
          >
            <div
              className={`w-12 h-12 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center shadow-lg`}
            >
              <value.icon className="w-6 h-6 text-white" />
            </div>

            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                {value.title}
              </h4>
              <p className="text-gray-700 leading-relaxed">
                {value.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>

  </div>
</div>


            {/* ==============================
                MISSION & VISION FULL WIDTH
            =============================== */}
            <motion.div
              variants={containerVariants}
              className="grid md:grid-cols-2 gap-8 mb-16 mt-10 w-full"
            >
              {/* MISSION */}
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-amber-50 to-orange-50
                rounded-2xl p-8 border border-amber-200 shadow-sm"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500
                  rounded-xl flex items-center justify-center">
                    <FaLightbulb className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  To provide cutting-edge IT development, AI-powered automation, digital marketing, and branding solutions for start-ups, SMEs, and enterprises. Our mission is to help businesses transform, scale, and thrive in a digital-first world with innovative, reliable, and result-driven strategies.
                </p>
              </motion.div>

              {/* VISION */}
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-amber-50 to-orange-50
                rounded-2xl p-8 border border-amber-200 shadow-sm"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-400
                  rounded-xl flex items-center justify-center">
                    <FaGlobe className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  To become a global leader in delivering innovative IT and digital marketing solutions. Empowering businesses across industries to achieve growth, efficiency, and lasting impact through creativity and technology.
                </p>
              </motion.div>
            </motion.div>

            {/* CTA */}
            <motion.div
              variants={itemVariants}
              className="text-center bg-gradient-to-r
              from-amber-500 to-orange-500 rounded-2xl p-12 relative"
            >
              <h3 className="text-3xl font-bold text-white mb-4">
                Ready to Transform Your Digital Presence?
              </h3>

              <p className="text-amber-100 text-lg mb-8 max-w-2xl mx-auto">
                Join hundreds of satisfied clients who have achieved remarkable 
                growth with our expert digital marketing solutions.
              </p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => (window.location.href = "contact-us")}
                className="inline-flex items-center gap-3 bg-white text-amber-600
                font-semibold px-8 py-4 rounded-xl hover:bg-gray-50"
              >
                Start Your Project
                <FaArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>

          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AboutUs;
